import { useCallback, useEffect, useState } from 'react';
import type { FilterLine, FilterValue } from '../types';
import { hasIncompleteFilter } from '../vyTableFilterUtils';

const createEmptyFilter = (): FilterLine => ({
  id: crypto.randomUUID(),
  field: '',
  operator: '',
  value: [],
});

const getInitialFilters = (defaultFilters: FilterLine[]): FilterLine[] => {
  const filters = defaultFilters ?? [];
  return filters.length === 0 ? [createEmptyFilter()] : filters;
};

export const useFilterState = (defaultFilters: FilterLine[] = []) => {
  const [filterLines, setFilterLines] = useState<FilterLine[]>(() => getInitialFilters(defaultFilters));

  useEffect(() => {
    setFilterLines((prev) => {
      const safeDefaultFilters = defaultFilters ?? [];
      const allFilterIds = safeDefaultFilters.map((filter) => filter.id);
      const validDefaultFilterIds = new Set(allFilterIds);
      const incompleteFiltersFromPrev = prev.filter(
        (f) => hasIncompleteFilter(f) && !validDefaultFilterIds.has(f.id),
      );
      const mergedFilters = [...safeDefaultFilters, ...incompleteFiltersFromPrev];

      return mergedFilters.length === 0 ? [createEmptyFilter()] : mergedFilters;
    });
  }, [defaultFilters]);

  const addFilter = useCallback(() => {
    const newFilterLine = createEmptyFilter();
    setFilterLines((prev) => [...(prev ?? []), newFilterLine]);
    return newFilterLine;
  }, []);

  const deleteFilter = useCallback((filterId: string) => {
    setFilterLines((prev) => (prev ?? []).filter((filter) => filter.id !== filterId));
  }, []);

  const updateFilter = useCallback((filterId: string, field: keyof FilterLine, value: FilterValue) => {
    setFilterLines((prev) =>
      (prev ?? []).map((filter) => (filter.id === filterId ? { ...filter, [field]: value } : filter)),
    );
  }, []);

  const resetFilters = useCallback(() => {
    setFilterLines([createEmptyFilter()]);
  }, []);

  return {
    filterLines,
    addFilter,
    deleteFilter,
    updateFilter,
    resetFilters,
  };
};
